/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/** First tick and steady-state cadence are identical — ~5 minutes (spec §3.2). */
export const HEALTH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Periodic mid-session heartbeat timer for the `playbackHealth` operational
 * event. `start()` cancels any prior timer so a new session's cadence is
 * always fresh; `stop()` is idempotent (safe to call when already stopped
 * or never started).
 */
export class PlaybackHealthLogger {
  private mSetIntervalFn: (
    handler: () => void,
    timeoutMs: number
  ) => ReturnType<typeof setInterval>;

  private mClearIntervalFn: (handle: ReturnType<typeof setInterval>) => void;

  private mIntervalId: ReturnType<typeof setInterval> | null = null;

  public constructor(
    setIntervalFn: (
      handler: () => void,
      timeoutMs: number
    ) => ReturnType<typeof setInterval> = (handler, timeoutMs) =>
      setInterval(handler, timeoutMs),
    clearIntervalFn: (handle: ReturnType<typeof setInterval>) => void = (
      handle
    ) => clearInterval(handle)
  ) {
    this.mSetIntervalFn = setIntervalFn;
    this.mClearIntervalFn = clearIntervalFn;
  }

  /** Starts the health-ping timer, cancelling any timer already running. */
  public start(onTick: () => void): void {
    this.stop();
    this.mIntervalId = this.mSetIntervalFn(onTick, HEALTH_INTERVAL_MS);
  }

  public stop(): void {
    if (this.mIntervalId !== null) {
      this.mClearIntervalFn(this.mIntervalId);
      this.mIntervalId = null;
    }
  }
}
